import { formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { uploadCustomerSpaceIsTermsUpdateGroup } from '@core/core.actions';
import { CustomerSpaceService } from '@core/customer-space/customer-space.service';
import { ReportService } from '@core/report/report.service';
import { select, Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { LinkButtonComponent } from '@platform/components/buttons/link-button/link-button.component';
import { FileUploadDialogComponent } from '@platform/components/file-upload-widget/file-upload/file-upload-dialog/file-upload-dialog.component';
import { DialogV2Service } from '@platform/dialog/dialog-v2.service';
import { selectPortalConfig } from '@platform/platform.selectors';
import { PortalConfig } from '@platform/platform.state';
import { pendingFlag, withPendingFlag } from '@platform/shared/utilities/pending-flag';
import { ToastService } from '@platform/toast/toast.service';
import { RootState } from '@root/app.state';
import saveAs from 'file-saver';
import { shareReplay } from 'rxjs';
import { AdminActionItemComponent } from '../ui/admin-action-item.component';
import { AdminPageSectionComponent } from '../ui/admin-page-section.component';
import { ButtonIndicatorDownloadComponent } from '../ui/button-indicator-download.component';
import { ButtonIndicatorUploadComponent } from '../ui/button-indicator-upload.component';
import { EditTermsPageDialogComponent } from './edit-terms-page-dialog/edit-terms-page-dialog.component';

@Component({
  selector: 'app-terms-updates-section',
  imports: [
    AdminPageSectionComponent,
    LinkButtonComponent,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslateModule,
    AdminActionItemComponent,
    ButtonIndicatorDownloadComponent,
    ButtonIndicatorUploadComponent,
  ],
  templateUrl: './terms-updates-section.component.html',
  styleUrl: './terms-updates-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TermsUpdatesSectionComponent {
  public readonly termsUpdatesReportPending = pendingFlag();
  public readonly portalConfig: Signal<PortalConfig | null | undefined>;

  constructor(
    private store$: Store<RootState>,
    private destroyRef: DestroyRef,
    private reportService: ReportService,
    private toastService: ToastService,
    private formBuilder: FormBuilder,
    private dialogService: DialogV2Service,
    private custService: CustomerSpaceService,
  ) {
    // this.latestTemplateUpdates = toSignal(store$.pipe(select(selectTemplateActualisation), filterSucceededResource()));

    // this.extractPending = toSignal(
    //   store$.pipe(
    //     select(selectSharepointTemplateExtraction),
    //     map((state) => state.status === ResourceStatuses.PENDING),
    //   ),
    //   { initialValue: false },
    // );

    this.portalConfig = toSignal(store$.pipe(select(selectPortalConfig)));
  }

  public downloadTermsUpdatesReport() {
    this.reportService
      .getTermsUpdatesReport()
      .pipe(withPendingFlag(this.termsUpdatesReportPending))
      .subscribe({
        next: (response) => saveAs(response, `Terms_Updates_Report_MYO2_${formatDate(new Date(), 'yyyyMMdd', 'en')}.xlsx`),
        error: () => this.toastService.showErrorToast('admin.reports.error-message'),
        complete: () => this.toastService.showSuccessToast('admin.reports.success-message'),
      });
  }

  openXlsxCustomerSpaceIdsTermsFileUploadDialog() {
    const portalConfig = this.portalConfig();
    if (!portalConfig) {
      return;
    }

    this.dialogService
      .open(FileUploadDialogComponent, {
        data: {
          multiple: false,
          invalidCharacters: portalConfig.invalidFileAndFolderNameCharacters,
          allowedFileFormats: ['xlsx'],
        },
      })
      .afterClosed()
      .subscribe((value) => {
        if (value) {
          this.toastService.showPendingToast('admin.batch-creation.upload-success-message');
          this.store$.dispatch(
            uploadCustomerSpaceIsTermsUpdateGroup.uploadTermsUpdateCustomerSpacedIDsFile({
              files: value.files,
            }),
          );
        }
      });
  }

  openEditTerms(language: string) {
    this.custService
      .getTermsPage(language)
      .pipe(shareReplay(1))
      .subscribe((dto: any) => {
        this.dialogService
          .open(EditTermsPageDialogComponent, {
            data: {
              language,
              rawHtml: dto?.rawHtml ?? '',
            },
          })
          .afterClosed()
          .subscribe((value) => console.log(value));
      });
  }
}
